import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { ChecklistDropdown, EventsDropdown } from '../home/WeatherMapControls';
import WeatherLayerSwitch from './WeatherLayerSwitch';
import { countryLabel, countrySummary } from '../../../utils/countryFilters';
import { chipSummary, toggleSelection } from '../../../utils/shipmentChipFilters';
import { STATUS_COLORS as SHIPMENT_STATUS_COLORS } from './modeIcons';
import { growingStatusColor, growingStatusLabel } from './growingStatuses';
import { LAYER_MODES } from '../../../utils/mapUrlParams';
import { floatingSurfaceClass } from '../floatingSurfaceStyles';

/** English fallbacks for the Shipment status chip; keys match the icon states. */
const SHIPMENT_STATUS_LABELS = {
  on_time: 'On time',
  ahead: 'Ahead',
  at_port: 'At port',
  delayed: 'Delayed',
  discharged: 'Discharged',
};

/** English fallbacks for the Mode chip; keys match MODE_KEYS. */
const MODE_LABELS = { sea: 'Sea', air: 'Air', truck: 'Truck' };

const CHIP_BASE = 'flex h-[32px] shrink-0 cursor-pointer items-center gap-[6px] rounded-[16px] border border-solid px-[11px] transition-colors';

/**
 * The map's filter chip row: Shipment status, Importer, Exporter, Origin,
 * Destination, Mode.
 *
 * Desktop shows all six side by side. Phones have no room for that, so the row
 * collapses behind a "+ Filter" chip and expands in place when tapped.
 */
const MapFilterChips = ({
  // Fullscreen only: keep every filter on one line, per the design rule.
  singleLine = false,
  chipFilters = null,
  originFilter = null,
  destFilter = null,
  modeFilter = null,
  // Growing draws regions, not shipments, so it gets its own chips rather than
  // the shipment set — filtering by importer or vessel mode says nothing about
  // a producing region, and leaving those chips up invited filters that
  // silently did nothing.
  layerMode = null,
  // When a saved view is active this is its layer set; the chip groups follow
  // it instead of `layerMode`. Null on the built-in layers.
  activeLayers = null,
  productionStatusFilter = null,
  // Weather's own chips. The tile layer and the hazard types are the only
  // filters this layer has — commodity and status stay in the strip above,
  // where every layer reads them.
  activeLayer = null,
  onLayerChange = () => {},
  activeEventTypes = [],
  onEventTypeToggle = () => {},
}) => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Origin / Destination checklist. All options selected -> "All"; otherwise
  // names on >=sm (first 2 + "+N") and a count below sm.
  const renderCountryDropdown = (label, filter, align) => {
    if (!filter) return null;
    const { keys, selectedKeys, mode, onChange, onSelectAll } = filter;
    const n = selectedKeys.length;
    const text = countrySummary(selectedKeys, mode);
    const summary = text.kind === 'names' ? (
      <>
        <span className="hidden sm:inline">{text.names}</span>
        <span className="sm:hidden">({n})</span>
      </>
    ) : t(`map.filters.${text.kind}`, text.kind === 'all' ? 'All' : 'None');
    return (
      <ChecklistDropdown
        variant="pill"
        label={label}
        count={n}
        summary={summary}
        allActive={mode === 'all'}
        align={align}
        clearLabel={t('map.filters.clear', 'Clear')}
        listClassName="max-h-[min(320px,40svh)] overflow-y-auto"
        items={keys.map((k) => ({ id: k, label: countryLabel(k) }))}
        selectedIds={selectedKeys}
        onToggle={(id) => onChange(toggleSelection(mode === 'all' ? null : selectedKeys, id))}
        onSelectAll={onSelectAll}
        onSelectNone={() => onChange([])}
        searchable
      />
    );
  };

  // `selected == null` is unrestricted, so the dropdown shows every row ticked
  // and the trigger reads "All".
  const renderChecklistChip = (label, chip, options, align = 'left', searchable = false) => {
    if (!chip) return null;
    const selected = chip.selected ?? options.map((o) => o.id);
    const text = chipSummary(chip.selected, options);
    const summary = text.kind === 'names'
      ? (
        <>
          <span className="hidden sm:inline">{text.names}</span>
          <span className="sm:hidden">({selected.length})</span>
        </>
      )
      : t(`map.filters.${text.kind}`, text.kind === 'all' ? 'All' : 'None');
    return (
      <ChecklistDropdown
        variant="pill"
        label={label}
        count={selected.length}
        summary={summary}
        allActive={chip.selected == null || selected.length === options.length}
        align={align}
        clearLabel={t('map.filters.clear', 'Clear')}
        listClassName="max-h-[min(320px,40svh)] overflow-y-auto"
        items={options}
        selectedIds={selected}
        onToggle={chip.onToggle}
        onSelectAll={chip.onSelectAll}
        onSelectNone={chip.onSelectNone}
        searchable={searchable}
        searchPlaceholder={t('map.filters.searchPlaceholder', 'Search...')}
      />
    );
  };

  const statusChipOptions = (chipFilters?.status?.keys || []).map((id) => ({
    id,
    label: t(`map.filters.shipmentStatus.${id}`, SHIPMENT_STATUS_LABELS[id]),
    color: SHIPMENT_STATUS_COLORS[id],
  }));

  // Transport mode (Sea / Air / Truck). Same `{keys, selectedKeys, mode}` shape
  // as the country filters — it is URL-backed too — but its labels are
  // translated rather than looked up in the country table.
  const renderModeDropdown = () => {
    if (!modeFilter) return null;
    const { keys, selectedKeys, mode, onChange, onSelectAll } = modeFilter;
    const options = keys.map((k) => ({ id: k, label: t(`map.filters.${k}`, MODE_LABELS[k]) }));
    const text = chipSummary(mode === 'all' ? null : selectedKeys, options);
    const summary = text.kind === 'names' ? (
      <>
        <span className="hidden sm:inline">{text.names}</span>
        <span className="sm:hidden">({selectedKeys.length})</span>
      </>
    ) : t(`map.filters.${text.kind}`, text.kind === 'all' ? 'All' : 'None');
    return (
      <ChecklistDropdown
        variant="pill"
        label={t('map.filters.mode', 'Mode')}
        count={selectedKeys.length}
        summary={summary}
        allActive={mode === 'all'}
        align="left"
        clearLabel={t('map.filters.clear', 'Clear')}
        items={options}
        selectedIds={selectedKeys}
        onToggle={(id) => onChange(toggleSelection(mode === 'all' ? null : selectedKeys, id))}
        onSelectAll={onSelectAll}
        onSelectNone={() => onChange([])}
      />
    );
  };

  // Growing's own chips. Production status reads the same `status` URL param
  // the strip above binds to, so the two can never disagree — the strip hides
  // its Status control on this layer rather than mirroring one filter twice.
  const productionStatusOptions = (productionStatusFilter?.keys || []).map((id) => ({
    id,
    label: t(`map.filters.productionStatus.${id}`, growingStatusLabel(id)),
    color: growingStatusColor(id),
  }));

  // Is this control actually narrowing the map? `selected == null` is
  // unrestricted, and so is a full set — both read as "All" on the pill.
  const chipIsNarrowed = (chip, options) => {
    if (!chip) return false;
    const selected = chip.selected ?? options.map((o) => o.id);
    return chip.selected != null && selected.length !== options.length;
  };
  const listIsNarrowed = (f) => Boolean(f) && f.mode !== 'all';

  // One flex child, not two, so the 12px the design puts between the switch
  // and Hazards survives the 8px gap this row uses for everything else.
  // Never splits internally: as one item it moves to the next line whole
  // rather than leaving Hazards orphaned from the switch it belongs to.
  const weatherChips = (
    /* Wraps below lg so Hazards drops to a line of its own, per the design:
       kept on the switch's line it ran under the layer rail, which is flush
       right and 48px wide, so its right-hand end was unreachable. From lg the
       row has the width to hold both and stays on one line. */
    <div className="flex w-full min-w-0 items-center gap-[12px] max-lg:flex-wrap max-lg:gap-y-[8px] lg:flex-nowrap">
      {/* `min-w-0` is what makes the switch responsive: it already carries
          `max-w-full` and `overflow-x-auto`, but a flex item's automatic
          minimum size is its content, so without this it refused to go below
          its full width and pushed Hazards off the row instead of scrolling
          its own chips. It shrinks but never grows — at full width the pill
          stays its natural size rather than stretching across the map. */}
      <WeatherLayerSwitch value={activeLayer} onChange={onLayerChange} className="min-w-0" />
      <div className="shrink-0">
        <EventsDropdown
          variant="pill"
          label={t('map.filters.hazards', 'Hazards')}
          activeTypes={activeEventTypes}
          onToggle={onEventTypeToggle}
        />
      </div>
    </div>
  );

  // Each control as its own item rather than one fragment per layer, because
  // the collapsed row keeps the narrowed ones on screen and has to pick them
  // out individually. `narrowed` is the same test the pill's own count badge
  // uses, so the collapsed and expanded rows can never disagree.
  const itemsFor = {
    shipments: () => [
      { key: 'status', narrowed: chipIsNarrowed(chipFilters?.status, statusChipOptions), node: renderChecklistChip(t('map.filters.shipmentStatus.label', 'Shipment status'), chipFilters?.status, statusChipOptions) },
      { key: 'importer', narrowed: chipIsNarrowed(chipFilters?.importer, chipFilters?.importer?.options || []), node: renderChecklistChip(t('map.filters.importer', 'Importer'), chipFilters?.importer, chipFilters?.importer?.options || [], 'left', true) },
      { key: 'exporter', narrowed: chipIsNarrowed(chipFilters?.exporter, chipFilters?.exporter?.options || []), node: renderChecklistChip(t('map.filters.exporter', 'Exporter'), chipFilters?.exporter, chipFilters?.exporter?.options || [], 'left', true) },
      { key: 'origin', narrowed: listIsNarrowed(originFilter), node: renderCountryDropdown(t('map.filters.origin', 'Origin'), originFilter, 'left') },
      { key: 'destination', narrowed: listIsNarrowed(destFilter), node: renderCountryDropdown(t('map.filters.destination', 'Destination'), destFilter, 'left') },
      { key: 'mode', narrowed: listIsNarrowed(modeFilter), node: renderModeDropdown() },
    ],
    growing: () => [
      {
        key: 'productionStatus',
        narrowed: chipIsNarrowed(productionStatusFilter, productionStatusOptions),
        node: renderChecklistChip(t('map.filters.productionStatus.label', 'Production status'), productionStatusFilter, productionStatusOptions),
        // Keys the region pins the map is drawing, so like the weather switch
        // it stays out of the phone's collapse — behind "+ Filter" there was
        // nothing on a phone to say what the pins meant or to change it.
        alwaysVisible: true,
      },
    ],
    // The layer switch always has exactly one value, so it narrows nothing;
    // the group counts as selected when a hazard type is on.
    weather: () => [
      {
        key: 'weather',
        narrowed: activeEventTypes.length > 0,
        node: weatherChips,
        // The switch is a view control, not a filter: it is always on, always
        // worth seeing, and too wide to share a line. It gets a full row of
        // its own under the commodity selector, and stays out of the phone's
        // collapse so "+ Filter" never hides the map's own legend key.
        ownLine: true,
        alwaysVisible: true,
      },
    ],
  };

  // A saved view draws several layers at once, so it gets each of their chip
  // groups rather than one layer's. Keying off `layerMode` alone would show
  // the filters for whichever single layer happened to be selected before the
  // view was activated — weather's layer switch above a map of shipments.
  // Weather draws region pins for context, not as its subject, so it carries
  // no production filter — the design gives that chip to Growing and to All,
  // where the regions are something you are actually working with.
  const growingChipsApply = layerMode !== LAYER_MODES.WEATHER;

  const activeGroupKeys = Array.isArray(activeLayers) && activeLayers.length
    ? [
      activeLayers.includes(LAYER_MODES.GROWING) && growingChipsApply ? 'growing' : null,
      activeLayers.includes(LAYER_MODES.SHIPMENTS) ? 'shipments' : null,
      activeLayers.includes(LAYER_MODES.WEATHER) ? 'weather' : null,
    ].filter(Boolean)
    : [{ [LAYER_MODES.GROWING]: 'growing', [LAYER_MODES.WEATHER]: 'weather' }[layerMode] || 'shipments'];

  const chipItems = activeGroupKeys
    .flatMap((k) => itemsFor[k]())
    .filter((i) => i.node);

  const narrowedItems = chipItems.filter((i) => i.narrowed);
  const activeFilterCount = narrowedItems.length;
  const collapsedItems = chipItems.filter((i) => i.narrowed || i.alwaysVisible);

  const renderItems = (items) => items.map((i) => (
    <React.Fragment key={i.key}>{i.node}</React.Fragment>
  ));

  return (
    <>
      {/* Phone: one chip that reveals the rest below it. */}
      {/* `w-full`, not `w-fit`: the row takes the overlay column's width so it
          is a real constraint. Sized to content it simply grew past the
          column, and the weather switch — which shrinks only when something
          above it is narrower than its content — ran off the screen edge
          instead of scrolling its own chips. */}
      <div className="flex w-full flex-wrap items-start gap-[8px] pr-[56px] lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((o) => !o)}
          aria-expanded={mobileOpen}
          className={`${CHIP_BASE} ${floatingSurfaceClass(94)} ${
            mobileOpen || activeFilterCount > 0 ? 'border-bloom-green' : ''
          }`}
        >
          {/* The count replaces the plus while filters are on: the badge is
              the same one the pills use, so a collapsed row reads the way an
              expanded one does. Closed and unfiltered, it is "+ Filter". */}
          {activeFilterCount > 0 && !mobileOpen ? (
            <span className="flex h-[16px] min-w-[16px] shrink-0 items-center justify-center rounded-full bg-[#F0FDF4] px-[4px] text-[10px] font-extrabold text-[#16A34A] dark:bg-[#1C3023] dark:text-[#7EE6A4]">
              {activeFilterCount}
            </span>
          ) : (
            <Plus size={14} className="shrink-0 text-bloom-green" />
          )}
          <span className="font-['Inter'] text-[13px] font-semibold whitespace-nowrap text-[#374151] dark:text-[#E6E8E6]">
            {t('map.filters.filter', 'Filter')}
          </span>
        </button>
        {/* Collapsed, two things stay on screen beside the button: whatever
            is actually narrowing the map (hiding it left no way to see what
            was filtering the view, or undo it, without opening the row), and
            anything flagged `alwaysVisible` — the weather switch, which is a
            view control rather than a filter. The rest appear on tap. */}
        {renderItems(mobileOpen ? chipItems : collapsedItems)}
      </div>

      {/* Fullscreen hoists this row into the filter strip, where the rule is
          one line of filters with the pop-up pills (Arrivals, Alerts) beneath.
          Wrapping there made a two-line block that nothing else could align
          to. `overflow-x-auto` is the escape hatch: on a screen too narrow for
          every chip the row scrolls rather than silently becoming two lines
          again. Outside fullscreen the chips have their own row and wrap
          normally. */}
      {singleLine ? (
        <div className="hidden w-full min-w-0 flex-nowrap items-start gap-[8px] overflow-x-auto lg:flex">
          {renderItems(chipItems)}
        </div>
      ) : (
        /* Windowed: one wrapping row, so the chips fill the width available
           and only take a second line when they have to. Giving each layer a
           line of its own read as a block but spent two lines on a strip that
           often fits in one. What made the old wrapping look ragged was the
           weather group splitting mid-group — it is `flex-nowrap` now, so it
           moves down whole and each line still starts at the left edge. */
        <div className="hidden w-full flex-wrap items-start gap-[8px] lg:flex">
          {chipItems.map((i) => (
            <div
              key={i.key}
              className={i.ownLine ? 'flex w-full items-start' : 'contents'}
            >
              {i.node}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default MapFilterChips;
